/**
 * AddressCorrectionModal Component
 * SESSION_201: Modal for submitting address correction requests
 * SESSION_202: Updated to use individual address fields
 *
 * Allows business owners to request corrections to their business address.
 * Submissions are reviewed by admins before being applied.
 */

import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  TextInput,
  Alert,
  Box,
  Grid
} from '@mantine/core';
import { IconMapPin, IconInfoCircle, IconBuilding } from '@tabler/icons-react';
import type { AddressCorrectionModalProps } from './AddressCorrectionModal.types';
import { useSubmitAddressCorrectionMutation } from '../../../../application/queries/onboardingQueries';

/** Address field errors */
interface AddressErrors {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

/** Parsed address components */
interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Parse a formatted address string into components
 * Handles formats like:
 * - "123 Main St, Seattle, WA 98101"
 * - "123 Main St, Seattle, 98101"
 * - "1505, 10th Avenue, Seattle, 98122" (street number separated)
 */
const parseAddress = (address: string): ParsedAddress => {
  if (!address) {
    return { street: '', city: '', state: '', zip: '' };
  }

  // Split by comma and clean up whitespace
  let parts = address.split(',').map(p => p.trim());

  // Handle edge case: street number separated from street name by comma
  // e.g., "1505, 10th Avenue" should become "1505 10th Avenue"
  if (parts.length >= 2 && /^\d+$/.test(parts[0] ?? '')) {
    // First part is just a number - combine with second part as street
    parts = [`${parts[0]} ${parts[1]}`, ...parts.slice(2)];
  }

  // Try to extract ZIP code (5 digits, possibly with +4)
  const zipRegex = /\b(\d{5}(-\d{4})?)\b/;

  // Default values
  let street = '';
  let city = '';
  let state = '';
  let zip = '';

  if (parts.length >= 2) {
    // Street is typically the first part(s)
    street = parts[0] ?? '';

    // Last part might contain "State ZIP" or just "ZIP"
    const lastPart = parts[parts.length - 1] ?? '';
    const zipMatch = lastPart.match(zipRegex);

    if (zipMatch) {
      zip = zipMatch[1] ?? '';
      // Check if there's a state code before the ZIP
      const stateAndZip = lastPart.trim();
      const stateMatch = stateAndZip.match(/^([A-Z]{2})\s+\d{5}/);
      if (stateMatch) {
        state = stateMatch[1] ?? '';
      }
    }

    // City is typically the second-to-last part (or second part if only 2 parts)
    if (parts.length >= 3) {
      // Format: "Street, City, State ZIP" or "Street, City, ZIP"
      city = parts[parts.length - 2] ?? '';
      // If we have 4+ parts, street might be multiple parts
      if (parts.length >= 4) {
        street = parts.slice(0, parts.length - 2).join(', ');
      }
    } else if (parts.length === 2) {
      // Format: "Street, City ZIP" or similar
      const secondPart = parts[1] ?? '';
      // Try to separate city from state/zip
      const cityStateZipMatch = secondPart.match(/^(.+?)\s+([A-Z]{2})?\s*(\d{5}(-\d{4})?)$/);
      if (cityStateZipMatch) {
        city = cityStateZipMatch[1]?.trim() ?? '';
        state = cityStateZipMatch[2] ?? '';
        zip = cityStateZipMatch[3] ?? '';
      } else {
        city = secondPart.replace(zipRegex, '').trim();
      }
    }
  } else if (parts.length === 1) {
    // Single string, try to parse
    street = address;
  }

  // Infer state if not found - based on ZIP code prefix or city name
  if (!state) {
    // ZIP code prefix mapping for common areas
    const zipPrefixToState: Record<string, string> = {
      '98': 'WA',  // Washington
      '99': 'WA',  // Washington (some areas)
      '97': 'OR',  // Oregon
      '02': 'MA',  // Massachusetts (Boston area)
    };

    // City to state mapping for common cities
    const cityToState: Record<string, string> = {
      'seattle': 'WA',
      'tacoma': 'WA',
      'bellevue': 'WA',
      'spokane': 'WA',
      'portland': 'OR',
      'boston': 'MA',
      'cambridge': 'MA',
    };

    // Try ZIP prefix first
    if (zip.length >= 2) {
      const prefix = zip.substring(0, 2);
      state = zipPrefixToState[prefix] ?? '';
    }

    // Try city name if state still not found
    if (!state && city) {
      const normalizedCity = city.toLowerCase().trim();
      state = cityToState[normalizedCity] ?? '';
    }
  }

  return { street, city, state, zip };
};

/**
 * AddressCorrectionModal - Form for requesting address corrections
 */
export const AddressCorrectionModal: React.FC<AddressCorrectionModalProps> = ({
  opened,
  onClose,
  businessId,
  businessName,
  currentAddress,
  onSuccess
}): React.ReactElement => {
  // Parse the current address once
  const parsedCurrentAddress = useMemo(() => parseAddress(currentAddress), [currentAddress]);

  // Individual address fields - initialized empty, populated on open
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [errors, setErrors] = useState<AddressErrors>({});
  const [submitError, setSubmitError] = useState<string | undefined>();

  const submitMutation = useSubmitAddressCorrectionMutation();

  /**
   * Pre-populate form with current address when modal opens
   */
  useEffect(() => {
    if (opened) {
      setStreet(parsedCurrentAddress.street);
      setCity(parsedCurrentAddress.city);
      setState(parsedCurrentAddress.state);
      setZip(parsedCurrentAddress.zip);
      setErrors({});
      setSubmitError(undefined);
    }
  }, [opened, parsedCurrentAddress]);

  /**
   * Check if the address has been modified from the original
   */
  const hasAddressChanged = useMemo((): boolean => {
    const currentStreet = street.trim();
    const currentCity = city.trim();
    const currentState = state.trim().toUpperCase();
    const currentZip = zip.trim();

    const originalStreet = parsedCurrentAddress.street.trim();
    const originalCity = parsedCurrentAddress.city.trim();
    const originalState = parsedCurrentAddress.state.trim().toUpperCase();
    const originalZip = parsedCurrentAddress.zip.trim();

    return (
      currentStreet !== originalStreet ||
      currentCity !== originalCity ||
      currentState !== originalState ||
      currentZip !== originalZip
    );
  }, [street, city, state, zip, parsedCurrentAddress]);

  /**
   * Clear field error when user types
   */
  const clearFieldError = useCallback((field: keyof AddressErrors): void => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    if (submitError) {
      setSubmitError(undefined);
    }
  }, [errors, submitError]);

  /**
   * Validate all address fields
   */
  const validateFields = useCallback((): boolean => {
    const newErrors: AddressErrors = {};
    let isValid = true;

    // Street validation
    if (!street.trim()) {
      newErrors.street = 'Street address is required';
      isValid = false;
    }

    // City validation
    if (!city.trim()) {
      newErrors.city = 'City is required';
      isValid = false;
    }

    // State validation (2-letter code)
    const stateValue = state.trim().toUpperCase();
    if (!stateValue) {
      newErrors.state = 'State is required';
      isValid = false;
    } else if (!/^[A-Z]{2}$/.test(stateValue)) {
      newErrors.state = 'Use 2-letter state code (e.g., WA)';
      isValid = false;
    }

    // ZIP validation (5 digits or 5+4 format)
    const zipValue = zip.trim();
    if (!zipValue) {
      newErrors.zip = 'ZIP code is required';
      isValid = false;
    } else if (!/^\d{5}(-\d{4})?$/.test(zipValue)) {
      newErrors.zip = 'Enter 5-digit ZIP code';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }, [street, city, state, zip]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback((): void => {
    // Validate all fields
    if (!validateFields()) {
      return;
    }

    // Format address as single string for API
    const requestedAddress = `${street.trim()}, ${city.trim()}, ${state.trim().toUpperCase()} ${zip.trim()}`;

    // Submit correction request
    submitMutation.mutate(
      { businessId, requestedAddress },
      {
        onSuccess: (data) => {
          if (data.success) {
            // Reset all fields
            setStreet('');
            setCity('');
            setState('');
            setZip('');
            setErrors({});
            onClose();
            onSuccess?.();
          } else {
            setSubmitError(data.message ?? 'Failed to submit request');
          }
        },
        onError: () => {
          setSubmitError('Failed to submit request. Please try again.');
        }
      }
    );
  }, [businessId, street, city, state, zip, validateFields, submitMutation, onClose, onSuccess]);

  /**
   * Handle modal close - state will be reset on next open via useEffect
   */
  const handleClose = useCallback((): void => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconMapPin size={24} color="var(--mantine-color-blue-6)" />
          <Text fw={600} size="lg">Request Address Correction</Text>
        </Group>
      }
      centered
      size="lg"
    >
      <Stack gap="lg">
        {/* Info Alert */}
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          Your correction request will be reviewed by our team. Once approved, your business address
          will be updated. This usually takes 1-2 business days.
        </Alert>

        {/* Current Business Info */}
        <Box>
          <Group gap="xs" mb="xs">
            <IconBuilding size={16} color="gray" />
            <Text size="sm" fw={600} c="dimmed">Current Information</Text>
          </Group>
          <Stack gap="xs" ml="md">
            <Text size="sm">
              <Text span fw={500}>Business: </Text>
              {businessName}
            </Text>
            <Text size="sm">
              <Text span fw={500}>Current Address: </Text>
              {currentAddress || 'No address on file'}
            </Text>
          </Stack>
        </Box>

        {/* Address Input Fields */}
        <Stack gap="sm">
          <TextInput
            label="Street Address"
            placeholder="123 Main St"
            value={street}
            onChange={(e) => {
              setStreet(e.currentTarget.value);
              clearFieldError('street');
            }}
            error={errors.street}
            disabled={submitMutation.isPending}
            required
          />

          <Grid>
            <Grid.Col span={{ base: 12, sm: 5 }}>
              <TextInput
                label="City"
                placeholder="Seattle"
                value={city}
                onChange={(e) => {
                  setCity(e.currentTarget.value);
                  clearFieldError('city');
                }}
                error={errors.city}
                disabled={submitMutation.isPending}
                required
              />
            </Grid.Col>

            <Grid.Col span={{ base: 6, sm: 3 }}>
              <TextInput
                label="State"
                placeholder="WA"
                value={state}
                onChange={(e) => {
                  setState(e.currentTarget.value.toUpperCase());
                  clearFieldError('state');
                }}
                error={errors.state}
                disabled={submitMutation.isPending}
                maxLength={2}
                required
              />
            </Grid.Col>

            <Grid.Col span={{ base: 6, sm: 4 }}>
              <TextInput
                label="ZIP Code"
                placeholder="98101"
                value={zip}
                onChange={(e) => {
                  setZip(e.currentTarget.value);
                  clearFieldError('zip');
                }}
                error={errors.zip}
                disabled={submitMutation.isPending}
                maxLength={10}
                required
              />
            </Grid.Col>
          </Grid>

          {/* Submit error message */}
          {submitError && (
            <Text size="sm" c="red">
              {submitError}
            </Text>
          )}
        </Stack>

        {/* No changes message */}
        {!hasAddressChanged && (
          <Text size="sm" c="dimmed" ta="center">
            Make changes to the address fields above to submit a correction request.
          </Text>
        )}

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            color="gray"
            onClick={handleClose}
            disabled={submitMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={submitMutation.isPending}
            leftSection={<IconMapPin size={16} />}
            disabled={!hasAddressChanged}
          >
            Submit Request
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
